import React, { useState, useEffect, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ProductForm.scss';
import requestInstance from '../../../services/AuthService/AccessTokenUpdateService';
import { BASE_URL } from '../../../api/constants';
import AsyncSelect from 'react-select/async';
import ImageForm from '../../../components/ImageForm/ImageForm';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CategorySelector } from '../../../components/CategorySelector/CategorySelector';

// Initial state moved outside component to create a clean reference
const initialProductState = {
  name: '',
  alcohol_percentage: 0,
  bottle_size: '',
  production_year: new Date().getFullYear(),
  age: 0,
  proven_sustainable_product_line: false,
  available_for_sampling_service: false,
  price: 0,
  payment_conditions: '',
  numbers_in_stock: 0,
  description: '',
  makers: '',
  category_id: null,
  company_id: null, // Will be set on component load
  warehouses_contain: [],
  warehouses_contain_2: [],
  currency: 'EUR',
  subcategories: [],
  tags: [],
};

export const ProductForm = ({ setCurrentForm, productId = null }) => {
  const [product, setProduct] = useState({
    ...initialProductState,
    company_id: JSON.parse(localStorage.getItem('user'))?.company?.id || null,
  });

  const [warehouses, setWarehouses] = useState([]);
  const [warehousesV1, setWarehousesV1] = useState([]);
  const [productPicture, setProductPicture] = useState({});
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const companyId = product.company_id || JSON.parse(localStorage.getItem('user'))?.company?.id;
        
        if (!companyId) {
          setError('Company ID not found');
          return;
        }
        
        // Fetch warehouses from v2 endpoint for display and selection
        const warehousesUrl = `${BASE_URL}warehouses/v2/?company_id=${companyId}`;
        console.log('Fetching warehouses from v2 endpoint:', warehousesUrl);
        
        const warehousesResponse = await requestInstance.get(warehousesUrl);
        const warehousesData = warehousesResponse.data.results || [];
        console.log('Available v2 warehouses:', warehousesData);
        
        // Set warehouses from v2 endpoint for the dropdown
        setWarehouses(warehousesData);
        
        // Also fetch v1 warehouses for the original field
        const warehousesV1Url = `${BASE_URL}warehouses/?company=${companyId}`;
        console.log('Fetching warehouses from v1 endpoint:', warehousesV1Url);
        
        const warehousesV1Response = await requestInstance.get(warehousesV1Url);
        const warehousesV1Data = warehousesV1Response.data.results || [];
        console.log('Available v1 warehouses:', warehousesV1Data);
        
        // Set v1 warehouses for submission
        setWarehousesV1(warehousesV1Data);
        
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        setError(error.message || 'Failed to load warehouses');
      }
    };

    fetchWarehouses();
  }, [product.company_id]);

  useEffect(() => {
    // If we have warehouses but no selected warehouse in the product, pre-select the TYRRST Warehouse
    if (warehouses.length > 0 && (!product.warehouses_contain || product.warehouses_contain.length === 0)) {
      const tyrrstWarehouse = warehouses.find(w => w.name === "TYRRST Warehouse");
      if (tyrrstWarehouse) {
        console.log('Pre-selecting TYRRST Warehouse:', tyrrstWarehouse.id);
        handleWarehouseChange([{ value: tyrrstWarehouse.id, label: tyrrstWarehouse.name }]);
      }
    }
  }, [warehouses]);

  const handleCategorySelect = (categoryId) => {
    setProduct(prev => ({
      ...prev,
      category_id: categoryId,
      subcategories: [], // Reset subcategories when category changes
      tags: [], // Reset tags when category changes
    }));
    setError(null); // Clear any previous errors
  };

  const handleSubcategoryChange = (subcategoryValue) => {
    // Handle both event object and direct value cases
    const value = subcategoryValue && subcategoryValue.target 
      ? subcategoryValue.target.value 
      : subcategoryValue;
    
    // Log subcategory selection
    console.log('Subcategory selected:', value);
    
    if (value) {
      // Parse the ID as a number if it's a string
      const subcategoryId = typeof value === 'string' ? parseInt(value, 10) : value;
      
      if (!isNaN(subcategoryId)) {
        console.log('Setting subcategory ID:', subcategoryId);
        setProduct(prev => ({
          ...prev,
          subcategories: [subcategoryId],
          tags: [], // Reset tags when subcategory changes
        }));
      } else {
        console.error('Invalid subcategory ID:', value);
        setProduct(prev => ({
          ...prev,
          subcategories: [],
          tags: [], // Reset tags when subcategory changes
        }));
      }
    } else {
      console.log('No subcategory selected, clearing selection');
      setProduct(prev => ({
        ...prev,
        subcategories: [],
        tags: [], // Reset tags when subcategory changes
      }));
    }
  };

  const handleTagSelect = (tagIds) => {
    setProduct(prev => ({
      ...prev,
      tags: tagIds.filter(Boolean), // Filter out any null/undefined values
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleCheckboxChangeW = (selectedOptions) => {
    // Convert selectedOptions to array of warehouse IDs (as numbers)
    const selectedIds = selectedOptions ? selectedOptions.map(option => Number(option.value)) : [];
    
    // Get the current list of valid warehouse IDs from our warehouses state
    const validWarehouseIds = warehouses.map(w => w.id);
    console.log('Current valid warehouse IDs:', validWarehouseIds);
    
    // Validate each selected warehouse exists in our warehouses list
    const validWarehouses = selectedIds.filter(id => validWarehouseIds.includes(id));
    
    if (validWarehouses.length !== selectedIds.length) {
      const invalidIds = selectedIds.filter(id => !validWarehouseIds.includes(id));
      console.error('Invalid warehouse IDs selected:', invalidIds);
      const invalidCount = selectedIds.length - validWarehouses.length;
      toast.warning(`${invalidCount} selected warehouse(s) are no longer available`);
    }

    console.log('Setting warehouses_contain to:', validWarehouses);
    setProduct(prev => ({
      ...prev,
      warehouses_contain: validWarehouses,
      warehouses_contain_2: validWarehouses, // Keep both arrays in sync
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProductPicture({
        file,
        preview: URL.createObjectURL(file)
      });
    }
  };

  const loadWarehouses = async (inputValue = '') => {
    try {
      // Get the company ID from either the product or from localStorage
      const companyId = product.company_id || JSON.parse(localStorage.getItem('user'))?.company?.id;
      
      if (!companyId) {
        console.error('No company ID available to load warehouses');
        return [];
      }

      // Consistently use the v2 endpoint
      const url = `${BASE_URL}warehouses/v2/?company_id=${companyId}${inputValue ? `&search=${inputValue}` : ''}`;
      console.log('Loading warehouses from URL:', url);
      
      const response = await requestInstance.get(url);
      const warehousesData = response.data.results || [];
      
      console.log('Loaded warehouses:', warehousesData);
      
      // Update warehouses in state
      setWarehouses(warehousesData);
      
      // Format for the dropdown - make sure we have both value and label
      return warehousesData.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
      }));
    } catch (error) {
      console.error('Error loading warehouses:', error);
      toast.error('Failed to load warehouses');
      return [];
    }
  };

  const handleWarehouseChange = (selectedOptions) => {
    // Log the selected options for debugging
    console.log('Selected warehouse options:', selectedOptions);
    
    // Format the selected warehouses correctly
    const selectedWarehouses = selectedOptions ? selectedOptions.map(option => Number(option.value)) : [];
    console.log('Converted warehouse IDs:', selectedWarehouses);
    
    // Update both warehouse fields with selected values
    setProduct(prev => {
      console.log('Previous product state:', prev);
      const updatedProduct = {
        ...prev,
        warehouses_contain: selectedWarehouses,
        warehouses_contain_2: selectedWarehouses
      };
      console.log('Updated product state:', updatedProduct);
      return updatedProduct;
    });
  };

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // First, make sure product has a valid company_id
        const companyId = product.company_id || JSON.parse(localStorage.getItem('user'))?.company?.id;
        
        if (companyId && !product.company_id) {
          setProduct(prev => ({
            ...prev,
            company_id: companyId
          }));
        }
        
        // Load warehouses - make sure to await this to populate dropdown options
        const warehouseOptions = await loadWarehouses();
        console.log('Initial warehouse options:', warehouseOptions);
        
        // If this is a new product and we have a TYRRST Warehouse, pre-select it
        if (!productId && warehouseOptions.length > 0) {
          const tyrrstWarehouse = warehouseOptions.find(w => w.label === "TYRRST Warehouse");
          
          if (tyrrstWarehouse) {
            console.log('Pre-selecting TYRRST Warehouse:', tyrrstWarehouse.value);
            setProduct(prev => ({
              ...prev,
              warehouses_contain: [tyrrstWarehouse.value],
              warehouses_contain_2: [tyrrstWarehouse.value]
            }));
          }
        }
      } catch (error) {
        console.error('Error initializing component:', error);
      }
    };
    
    initializeComponent();
  }, []);  // Run once on component mount

  const handleDescriptionChange = (content) => {
    setProduct(prev => ({
      ...prev,
      description: content
    }));
  };

  const handleSave = async () => {
    try {
      // Refresh warehouses one last time before submission
      try {
        const companyId = product.company_id || JSON.parse(localStorage.getItem('user'))?.company?.id;
        
        // Refresh both v1 and v2 warehouses
        const refreshV2Url = `${BASE_URL}warehouses/v2/?company_id=${companyId}`;
        console.log('Refreshing v2 warehouses:', refreshV2Url);
        
        const refreshV2Response = await requestInstance.get(refreshV2Url);
        const refreshedV2Warehouses = refreshV2Response.data.results || [];
        setWarehouses(refreshedV2Warehouses);
        
        const refreshV1Url = `${BASE_URL}warehouses/?company=${companyId}`;
        console.log('Refreshing v1 warehouses:', refreshV1Url);
        
        const refreshV1Response = await requestInstance.get(refreshV1Url);
        const refreshedV1Warehouses = refreshV1Response.data.results || [];
        setWarehousesV1(refreshedV1Warehouses);
        
        console.log('V2 warehouse IDs:', refreshedV2Warehouses.map(w => w.id));
        console.log('V1 warehouse IDs:', refreshedV1Warehouses.map(w => w.id));
        
        // Find TYRRST Warehouse in both sets
        const tyrrstWarehouseV2 = refreshedV2Warehouses.find(w => w.name === "TYRRST Warehouse");
        const tyrrstWarehouseV1 = refreshedV1Warehouses.find(w => w.name === "TYRRST Warehouse");
        
        if (tyrrstWarehouseV2 && tyrrstWarehouseV1) {
          console.log('Found TYRRST Warehouse in v1:', tyrrstWarehouseV1.id);
          console.log('Found TYRRST Warehouse in v2:', tyrrstWarehouseV2.id);
          
          // If no warehouses are selected, select the TYRRST Warehouse in both versions
          if (!product.warehouses_contain || product.warehouses_contain.length === 0) {
            console.log('Auto-selecting TYRRST Warehouse for submission');
            setProduct(prev => ({
              ...prev,
              warehouses_contain: [tyrrstWarehouseV1.id],
              warehouses_contain_2: [tyrrstWarehouseV2.id]
            }));
            
            // Wait for state update
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing warehouses before save:', refreshError);
      }

      // Wait a moment to ensure state updates complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Enhanced validation
      const validationErrors = [];
      
      if (!product.name.trim()) {
        validationErrors.push('Product name is required');
      }
      
      if (!product.category_id) {
        validationErrors.push('Please select a category');
      }
      
      if (!product.subcategories.length) {
        validationErrors.push('Please select at least one subcategory');
      }
      
      if (product.price < 0) {
        validationErrors.push('Price cannot be negative');
      }
      
      if (product.numbers_in_stock < 0) {
        validationErrors.push('Stock quantity cannot be negative');
      }

      // Get current list of valid warehouse IDs from both v1 and v2
      const availableV1WarehouseIds = warehousesV1.map(w => w.id);
      const availableV2WarehouseIds = warehouses.map(w => w.id);
      
      console.log('Available v1 warehouse IDs at submission:', availableV1WarehouseIds);
      console.log('Available v2 warehouse IDs at submission:', availableV2WarehouseIds);
      
      // Map the selected v2 warehouse IDs to corresponding v1 IDs
      const selectedV2Ids = (product.warehouses_contain || [])
        .map(id => typeof id === 'string' ? parseInt(id, 10) : id);
      
      // Find v1 warehouses with matching names to our selected v2 warehouses
      const validV1Warehouses = [];
      const validV2Warehouses = [];
      
      // For each selected v2 warehouse ID
      for (const v2Id of selectedV2Ids) {
        // Find the v2 warehouse object
        const v2Warehouse = warehouses.find(w => Number(w.id) === Number(v2Id));
        
        if (v2Warehouse) {
          // Find the corresponding v1 warehouse with the same name
          const v1Warehouse = warehousesV1.find(w => w.name === v2Warehouse.name);
          
          // Add to valid lists if found
          if (v1Warehouse) {
            validV1Warehouses.push(v1Warehouse.id);
          }
          validV2Warehouses.push(v2Id);
        }
      }
      
      console.log('Valid v1 warehouses after mapping:', validV1Warehouses);
      console.log('Valid v2 warehouses after filtering:', validV2Warehouses);
      
      // If no valid warehouses found, try to use TYRRST Warehouse as fallback
      if (validV1Warehouses.length === 0 || validV2Warehouses.length === 0) {
        const tyrrstWarehouseV2 = warehouses.find(w => w.name === "TYRRST Warehouse");
        const tyrrstWarehouseV1 = warehousesV1.find(w => w.name === "TYRRST Warehouse");
        
        if (tyrrstWarehouseV2 && tyrrstWarehouseV1) {
          validV2Warehouses.push(tyrrstWarehouseV2.id);
          validV1Warehouses.push(tyrrstWarehouseV1.id);
        }
      }
      
      if (validationErrors.length > 0) {
        validationErrors.forEach(error => toast.error(error));
        
        // If the error was just about warehouses and we fixed it, continue
        if (validationErrors.length === 1 && validationErrors[0].includes('Using TYRRST Warehouse by default')) {
          // Continue with submission
        } else {
          return; // Stop submission for other errors
        }
      }

      // Prepare payload for backend (ensure warehouse_id is present)
      const payload = {
        ...product,
        warehouse_id: product.warehouses_contain && product.warehouses_contain.length > 0
          ? product.warehouses_contain[0]
          : null,
      };
      delete payload.warehouses_contain;
      delete payload.warehouses_contain_2;

      // For alcohol products, add the required fields
      let cleanProduct = {
        ...payload,
        warehouses_contain: validV1Warehouses,
        warehouses_contain_2: validV2Warehouses,
      };
      
      // Check if we need to include alcohol_percentage and bottle_size fields
      const alcoholCategories = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 38, 39, 40]; // Example IDs, adjust as needed
      
      if (alcoholCategories.includes(parseInt(cleanProduct.category_id, 10)) && 
          (!cleanProduct.alcohol_percentage || !cleanProduct.bottle_size)) {
        cleanProduct = {
          ...cleanProduct,
          alcohol_percentage: cleanProduct.alcohol_percentage || 0,  // Default to 0 if missing
          bottle_size: cleanProduct.bottle_size || 0  // Default to 0 if missing
        };
        console.log('Added required alcohol product fields');
      }
      
      const formData = new FormData();
      
      // Create a clean product object with validated warehouses
      Object.keys(cleanProduct).forEach(key => {
        if (Array.isArray(cleanProduct[key])) {
          if (key === 'warehouses_contain') {
            // Use valid v1 warehouse IDs
            console.log(`Adding ${key}:`, validV1Warehouses);
            validV1Warehouses.forEach(id => {
              formData.append(key, id.toString());
            });
          } else if (key === 'warehouses_contain_2') {
            // Use valid v2 warehouse IDs
            console.log(`Adding ${key}:`, validV2Warehouses);
            validV2Warehouses.forEach(id => {
              formData.append(key, id.toString());
            });
          } else {
            // Handle other arrays (subcategories, tags)
            cleanProduct[key]
              .map(value => typeof value === 'string' ? parseInt(value, 10) : value)
              .filter(value => !isNaN(value))
              .forEach(value => {
                formData.append(key, value.toString());
              });
          }
        } else if (typeof cleanProduct[key] === 'object' && cleanProduct[key] !== null) {
          formData.append(key, JSON.stringify(cleanProduct[key]));
        } else if (cleanProduct[key] !== null && cleanProduct[key] !== undefined) {
          formData.append(key, cleanProduct[key].toString());
        }
      });

      if (productPicture.file) {
        formData.append('image', productPicture.file);
      }

      const url = productId 
        ? `${BASE_URL}products/${productId}/`
        : `${BASE_URL}products/`;

      // Log the FormData contents for debugging
      console.log('Submitting product with warehouses:', validV2Warehouses);
      formData.forEach((value, key) => {
        console.log(`${key}:`, value);
      });

      const response = await requestInstance({
        method: productId ? 'PATCH' : 'POST',
        url,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success(`Product ${productId ? 'updated' : 'created'} successfully`);
        setCurrentForm('list');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to save product. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
    }
  };

  return (
    <div className="product-form">
      <ToastContainer />
      <h2>{productId ? 'Edit Product' : 'Add New Product'}</h2>
      
      {error && (
        <div className="product-form__error">
          {error}
        </div>
      )}
      
      <div className="form-group">
        <label htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={product.name}
          onChange={handleChange}
          required
          className={!product.name.trim() ? 'error' : ''}
        />
      </div>

      <div className="form-group">
        <label>Category *</label>
        <CategorySelector
          onCategorySelect={handleCategorySelect}
          onSubcategorySelect={handleSubcategoryChange}
          onTagSelect={handleTagSelect}
          selectedCategory={product.category_id}
          selectedSubcategories={product.subcategories}
          selectedTags={product.tags}
          disabled={false}
        />
      </div>

      <div className='ProductForm__section'>
        {productId ? <ImageForm objectId={productId} objectType='product' /> :

          <div className='CocktailForm__row'>
            <div className='CocktailForm__field'>
              <h3 className='CocktailForm__field_title'>Picture</h3>
              <input
                className='CocktailForm__input'
                type='file'
                name='picture'
                onChange={handleFileChange}
                ref={fileInputRef}
              />
            </div>
          </div>
        }
        <div className='ProductForm__row'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Alcohol Percentage</h3>
            <input className='ProductForm__input' type='number' name='alcohol_percentage' value={product.alcohol_percentage} onChange={handleChange} />
          </div>
        </div>
        <div className='ProductForm__row'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Bottle Size</h3>
            <input className='ProductForm__input' name='bottle_size' value={product.bottle_size} onChange={handleChange} />
          </div>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Price to Horeca</h3>
            <input className='ProductForm__input' min="0" type='number' name='price' value={product.price} onChange={handleChange} />
          </div>
        </div>
        <div className='ProductForm__row'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Production Year</h3>
            <input className='ProductForm__input' type='number' name='production_year' value={product.production_year} onChange={handleChange} />
          </div>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Age</h3>
            <input className='ProductForm__input' type='number' name='age' value={product.age} onChange={handleChange} />
          </div>
        </div>
        <div className='ProductForm__row'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Numbers in Stock</h3>
            <input className='ProductForm__input' type='number' name='numbers_in_stock' value={product.numbers_in_stock} onChange={handleChange} />
          </div>

          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Currency</h3>
            <select 
              className='ProductForm__input' 
              name='currency' 
              value={product.currency} 
              onChange={handleChange}
            >
              <option value="EUR">Euro (EUR)</option>
              <option value="USD">Dollar (USD)</option>
              <option value="NOK">Norwegian Krone (NOK)</option>
              <option value="DKK">Danish Krone (DKK)</option>
              <option value="GBP">Pound Sterling (GBP)</option>
              <option value="SEK">Swedish Krona (SEK)</option>
              <option value="PLN">Polish Zloty (PLN)</option>
              <option value="CZK">Czech Koruna (CZK)</option>
              <option value="HUF">Hungarian Forint (HUF)</option>
              <option value="RON">Romanian Leu (RON)</option>
              <option value="HRK">Croatian Kuna (HRK)</option>
              <option value="BGN">Bulgarian Lev (BGN)</option>
            </select>
          </div>

        </div>
        <div className='ProductForm__row'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Makers</h3>
            <input className='ProductForm__input' name='makers' value={product.makers} onChange={handleChange} />
          </div>
        </div>
        
        <div className='ProductForm__row ProductForm__field--long'>
          <div className='ProductForm__field'>
            <h3 className='ProductForm__field_title'>Select from which warehouse the product ships</h3>
            <AsyncSelect
              isMulti
              cacheOptions
              defaultOptions={true}
              loadOptions={loadWarehouses}
              onChange={handleWarehouseChange}
              value={product.warehouses_contain?.map(id => {
                const warehouse = warehouses.find(w => Number(w.id) === Number(id));
                return warehouse ? { value: warehouse.id, label: warehouse.name } : null;
              }).filter(Boolean)}
              placeholder="Select warehouse(s)..."
              className="warehouse-selector"
            />
          </div>
        </div>
        <div className='ProductForm__field--description'>
          <div className='ProductForm__field' style={{ width: '92%' }}>
            <h3 className='ProductForm__field_title'>Description</h3>
            <ReactQuill
              theme="snow"
              value={product.description}
              onChange={handleDescriptionChange}
            />
          </div>
        </div>
        <div className='ProductForm--checkbox'>
          <div className='ProductForm__field--checkbox'>
            <h3 className='ProductForm__field_title'>Proven Sustainable Product Line</h3>
            <input className='ProductForm__input--checkbox' type='checkbox' name='proven_sustainable_product_line' checked={product.proven_sustainable_product_line} onChange={handleCheckboxChange} />
          </div>
          <div className='ProductForm__field--checkbox' >
            <h3 className='ProductForm__field_title'>Available for Sampling Service</h3>
            <input className='ProductForm__input--checkbox' type='checkbox' name='available_for_sampling_service' checked={product.available_for_sampling_service} onChange={handleCheckboxChange} />
          </div>
        </div>
      </div>
      <div className='ProductForm__header'>
        <h2 className='ProductForm__title'>{productId ? 'Update Product' : 'Create Product'}</h2>
        <div className='ProductForm__header__buttons'>
          <button className='ProductForm__header__button ProductForm__header__button--cancel' onClick={() => setCurrentForm('products')}>Back</button>
          <button className='ProductForm__header__button ProductForm__header__button--save' onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;